import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { usePatients } from "@/lib/usePatients";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/clinical/PageHeader";
import { EmptyState } from "@/components/clinical/EmptyState";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { Plus, CalendarClock, Trash2, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "@/lib/safeToast";

const TYPES = ["primeira consulta","retorno","urgência","teleconsulta","registro retroativo","administrativo","monitoramento","consulta","crise","ajuste medicamentoso","recaída","evento adverso","sono","triagem","outro"];
const STATUSES = ["planejada", "realizada", "faltou", "cancelada", "remarcada"];

function toLocalInput(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v: string) {
  return v ? new Date(v).toISOString() : null;
}

interface SessionForm {
  id?: string;
  name: string;
  patient_id: string;
  session_type: string;
  session_status: string;
  session_at: string; // datetime-local
  is_retroactive: boolean;
}

const emptyForm = (patientPrefill: string): SessionForm => ({
  name: "", patient_id: patientPrefill, session_type: "retorno", session_status: "realizada",
  session_at: toLocalInput(new Date().toISOString()), is_retroactive: false,
});

export default function Sessions() {
  const [params] = useSearchParams();
  const patientPrefill = params.get("patient") ?? "";
  const autoNew = params.get("new") === "1";
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: patients = [] } = usePatients();
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: async () => (await supabase.from("clinical_sessions").select("*, patients(full_name)").order("session_at", { ascending: false })).data ?? [],
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<SessionForm>(emptyForm(patientPrefill));

  useEffect(() => {
    if (!autoNew || !patientPrefill) return;
    setForm(emptyForm(patientPrefill));
    setOpen(true);
  }, [autoNew, patientPrefill]);

  function openNew() { setForm(emptyForm(patientPrefill)); setOpen(true); }
  function openEdit(s: any) {
    setForm({
      id: s.id, name: s.name ?? "", patient_id: s.patient_id, session_type: s.session_type ?? "retorno",
      session_status: s.session_status ?? s.status ?? "realizada",
      session_at: toLocalInput(s.session_at), is_retroactive: !!s.is_retroactive,
    });
    setOpen(true);
  }

  async function save() {
    if (!form.name || !form.patient_id) return toast.error("Nome e paciente são obrigatórios");
    const iso = fromLocalInput(form.session_at);
    const retro = form.is_retroactive || (iso ? new Date(iso).getTime() < Date.now() - 15 * 60_000 : false);
    const payload: any = {
      patient_id: form.patient_id,
      name: form.name,
      session_type: form.session_type,
      session_at: iso,
      session_status: form.session_status,
      is_retroactive: retro,
    };
    if (form.id) {
      const { error } = await supabase.from("clinical_sessions").update(payload).eq("id", form.id);
      if (error) return toast.error(error.message);
      toast.success("Sessão atualizada");
    } else {
      payload.owner_id = user!.id;
      const { data, error } = await supabase.from("clinical_sessions").insert(payload).select().single();
      if (error) return toast.error(error.message);
      toast.success("Sessão criada");
      qc.invalidateQueries({ queryKey: ["sessions"] });
      setOpen(false);
      window.location.href = `/sessoes/${data.id}`;
      return;
    }
    qc.invalidateQueries({ queryKey: ["sessions"] });
    setOpen(false);
  }

  async function remove(id: string) {
    if (!confirm("Excluir sessão?")) return;
    await supabase.from("clinical_sessions").delete().eq("id", id);
    qc.invalidateQueries({ queryKey: ["sessions"] });
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Sessões clínicas" description="Sessão é um agrupador opcional de consulta/atendimento. Doses, check-ins, sinais vitais e eventos podem ser registrados sem sessão e permanecem visíveis no Journal/Timeline."
        actions={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Nova sessão</Button>}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? "Editar sessão" : "Nova sessão"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Paciente</Label>
              <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}>
                <option value="">Selecione…</option>
                {(patients as any[]).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Nome da sessão</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Revisão pós-ajuste estimulante" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Data / hora clínica</Label>
                <Input type="datetime-local" value={form.session_at} onChange={(e) => setForm({ ...form, session_at: e.target.value })} />
              </div>
              <div className="space-y-1.5"><Label>Retroativa</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={String(form.is_retroactive)} onChange={(e) => setForm({ ...form, is_retroactive: e.target.value === "true" })}>
                  <option value="false">Não</option>
                  <option value="true">Sim (registrando depois)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Tipo</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value })}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.session_status} onChange={(e) => setForm({ ...form, session_status: e.target.value })}>
                  {STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save}>{form.id ? "Salvar" : "Criar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {isLoading ? <div className="text-sm text-muted-foreground">Carregando…</div> :
      sessions.length === 0 ? (
        <EmptyState icon={<CalendarClock className="h-5 w-5" />} title="Nenhuma sessão" description="Isso não bloqueia nenhum registro longitudinal. Crie uma sessão apenas quando quiser agrupar uma consulta, retorno, crise ou outro atendimento." />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {(sessions as any[]).map((s) => (
            <Card key={s.id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Link to={`/sessoes/${s.id}`} className="font-medium hover:underline">{s.name}</Link>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.patients?.full_name} • {s.session_type}</div>
                  <div className="text-xs text-muted-foreground">Realizada em: {fmtDate(s.session_at)}</div>
                  <div className="text-[10px] text-muted-foreground">Criada em: {fmtDate(s.created_at)}</div>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  <Badge variant="outline" className="capitalize text-[10px]">{s.session_status ?? s.status}</Badge>
                  {s.is_retroactive ? <Badge variant="secondary" className="text-[10px]">retroativa</Badge> : null}
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
